import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AccountSettings } from "@/components/AccountSettings";

const tagUrl = (id) => `${window.location.origin}/tag/${id}`;

export default function AdminPage() {
  const [stats, setStats] = useState({ total: 0, activated: 0, unclaimed: 0, matched: 0, tags: [] });
  const [count, setCount] = useState(50);
  const [batch, setBatch] = useState([]);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/tags", { params: { status, q: query } });
      setStats(data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, query]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tags/generate", { count: Number(count) });
      setBatch(data);
      toast.success(`${data.length} tag IDs minted`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Clipboard unavailable — select and copy manually");
    }
  };

  const removeTag = async (tagId) => {
    if (!window.confirm(`Delete tag ${tagId}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/tags/${tagId}`);
      toast.success(`${tagId} deleted`);
      setBatch((b) => b.filter((t) => t.tag_id !== tagId));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const purge = async () => {
    const prefix = window.prompt("Delete every tag whose ID starts with:", "TEST");
    if (!prefix || prefix.trim().length < 2) return;
    if (!window.confirm(`Delete ALL tags starting with "${prefix.toUpperCase()}"? This cannot be undone.`))
      return;
    try {
      const { data } = await api.post("/admin/tags/purge", { prefix });
      toast.success(`${data.deleted} tags deleted`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (denied)
    return (
      <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="admin-denied">
        <h1 className="font-display text-3xl font-black tracking-tighter">Admin only</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          This factory console is restricted.{" "}
          <Link to="/dashboard" className="text-primary hover:underline">
            Back to dashboard
          </Link>
        </p>
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14" data-testid="admin-page">
      <p className="text-xs tracking-[0.25em] uppercase font-bold text-muted-foreground">
        Factory console
      </p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3">
        Tag inventory
      </h1>

      <div className="mt-10 grid gap-px sm:grid-cols-3 bg-border border border-border">
        {[
          ["Total minted", stats.total, "admin-stat-total"],
          ["Activated", stats.activated, "admin-stat-activated"],
          ["Unclaimed", stats.unclaimed, "admin-stat-unclaimed"],
        ].map(([label, value, tid]) => (
          <div key={label} className="bg-[#0A0A0A] p-8">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
              {label}
            </p>
            <p data-testid={tid} className="font-display text-4xl font-black tracking-tighter mt-3">
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
        <Input
          data-testid="admin-count-input"
          type="number"
          min={1}
          max={200}
          value={count}
          onChange={(e) => setCount(e.target.value)}
          className="h-12 w-full sm:w-28 rounded-none bg-[#141414] border-border focus:ring-2 focus:ring-primary text-white"
        />
        <Button
          onClick={generate}
          disabled={busy}
          data-testid="admin-generate-button"
          className="h-12 rounded-none bg-primary hover:bg-[#FF5C53] tracking-[0.15em] uppercase text-xs font-bold gap-2 px-6"
        >
          <Sparkles className="w-4 h-4" /> {busy ? "Minting" : `Generate ${count} IDs`}
        </Button>
      </div>

      {batch.length > 0 && (
        <div className="mt-8 border border-border bg-[#141414] p-6" data-testid="admin-batch-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
              New batch — {batch.length} URLs
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                data-testid="admin-copy-urls-button"
                onClick={() => copy(batch.map((t) => tagUrl(t.tag_id)).join("\n"), "URLs")}
                className="rounded-none border-border gap-2 text-xs uppercase font-bold h-9"
              >
                <Copy className="w-3.5 h-3.5" /> Copy URLs
              </Button>
              <Button
                variant="outline"
                data-testid="admin-copy-csv-button"
                onClick={() =>
                  copy(
                    ["tag_id,url", ...batch.map((t) => `${t.tag_id},${tagUrl(t.tag_id)}`)].join("\n"),
                    "CSV"
                  )
                }
                className="rounded-none border-border gap-2 text-xs uppercase font-bold h-9"
              >
                <Copy className="w-3.5 h-3.5" /> Copy CSV
              </Button>
            </div>
          </div>
          <pre
            data-testid="admin-batch-urls"
            className="mt-5 max-h-72 overflow-auto text-xs leading-relaxed text-muted-foreground whitespace-pre"
          >
            {batch.map((t) => tagUrl(t.tag_id)).join("\n")}
          </pre>
        </div>
      )}

      <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center">
        <Input
          data-testid="admin-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tag ID"
          className="h-11 w-full sm:max-w-xs rounded-none bg-[#141414] border-border focus:ring-2 focus:ring-primary text-white uppercase"
        />
        <div className="flex gap-2">
          {[
            ["all", "All"],
            ["activated", "Activated"],
            ["unclaimed", "Unclaimed"],
          ].map(([key, label]) => (
            <Button
              key={key}
              data-testid={`admin-filter-${key}`}
              onClick={() => setStatus(key)}
              variant={status === key ? "default" : "outline"}
              className={`h-11 rounded-none text-xs tracking-[0.1em] uppercase font-bold ${
                status === key
                  ? "bg-primary hover:bg-[#FF5C53]"
                  : "border-border hover:border-primary"
              }`}
            >
              {label}
            </Button>
          ))}
        </div>
        <p
          data-testid="admin-matched-count"
          className="text-xs tracking-[0.2em] uppercase text-muted-foreground sm:ml-auto"
        >
          {stats.matched} match{stats.matched === 1 ? "" : "es"}
        </p>
        <Button
          variant="outline"
          data-testid="admin-purge-button"
          onClick={purge}
          className="h-11 rounded-none border-border hover:border-primary hover:text-primary gap-2 text-xs tracking-[0.1em] uppercase font-bold"
        >
          <Trash2 className="w-3.5 h-3.5" /> Purge by prefix
        </Button>
      </div>

      <div className="mt-10 border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-inventory-table">
          <thead>
            <tr className="bg-[#141414] text-left">
              {["Tag ID", "Status", "Owner", "Name", ""].map((h, i) => (
                <th
                  key={h || i}
                  className="px-5 py-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stats.tags.map((t) => (
              <tr key={t.tag_id} className="border-t border-border" data-testid={`admin-row-${t.tag_id}`}>
                <td className="px-5 py-4">
                  <Link to={`/tag/${t.tag_id}`} className="font-bold hover:text-primary">
                    {t.tag_id}
                  </Link>
                </td>
                <td className="px-5 py-4">
                  <span className={t.claimed ? "text-[#34C759]" : "text-muted-foreground"}>
                    {t.claimed ? "Activated" : "Unclaimed"}
                  </span>
                </td>
                <td className="px-5 py-4 text-muted-foreground">{t.owner_email || "—"}</td>
                <td className="px-5 py-4 text-muted-foreground">{t.name || "—"}</td>
                <td className="px-5 py-4 text-right">
                  <Button
                    variant="ghost"
                    data-testid={`admin-delete-${t.tag_id}`}
                    onClick={() => removeTag(t.tag_id)}
                    className="h-9 w-9 p-0 rounded-none hover:text-primary"
                    title={`Delete ${t.tag_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {stats.tags.length === 0 && (
          <p
            data-testid="admin-no-results"
            className="px-5 py-10 text-center text-sm text-muted-foreground"
          >
            No tags match this filter.
          </p>
        )}
      </div>

      <AccountSettings title="Admin credentials" />
    </div>
  );
}
